import { quizQuestions } from '../data/quiz'
import { kuurProductsFor } from '../data/kuurProducts'
import { euro } from '../core/format/currency'
import type {
  Catalog,
  Concern,
  ConcernId,
  Product,
  Routine,
  SkinType,
  Treatment,
} from '../core/types/catalog'

/**
 * Alle opzoek- en koppelfuncties op de catalogus. Componenten krijgen deze via
 * useCatalog() en zoeken nooit zelf in de data — zo werkt alles gelijk,
 * of de catalogus nu lokaal staat of uit de backend komt.
 */

// ——— Opmaak (puur, zonder catalogus) ———

/** "30 ml · ± 3 maanden" — alleen de delen die bekend zijn. */
export const productMeta = (p: Pick<Product, 'volume' | 'usesPerBottle'>) =>
  [p.volume, p.usesPerBottle].filter(Boolean).join(' · ')

/** "3 maanden" of "4 tot 5 maanden". */
export const monthsLabel = ({ minMonths, maxMonths }: Routine['lasts']) =>
  minMonths === maxMonths
    ? `${minMonths} ${minMonths === 1 ? 'maand' : 'maanden'}`
    : `${minMonths} tot ${maxMonths} maanden`

/** "€ 99", of "Alleen als kuur" als er geen losse prijs is. */
export const treatmentPrice = (t: Treatment) => (t.price === null ? 'Alleen als kuur' : euro(t.price))

/** "75 min" */
export const treatmentDuration = (t: Treatment) => `${t.durationMinutes} min`

/** "6 × 80 min · € 635" */
export const kuurLabel = (t: Treatment) =>
  t.kuur ? `${t.kuur.sessions} × ${t.durationMinutes} min · ${euro(t.kuur.price)}` : null

const isDefined = <T,>(x: T | undefined): x is T => x !== undefined

// ——— Index op de catalogus ———

export function createCatalogIndex(catalog: Catalog) {
  const productMap = new Map(
    [...catalog.products, ...catalog.giftCards, ...kuurProductsFor(catalog.treatments)].map((p) => [p.id, p] as const),
  )

  const productById = (id: string) => productMap.get(id)
  const productsByIds = (ids: string[]) => ids.map(productById).filter(isDefined)
  const routineById = (id: string) => catalog.routines.find((r) => r.id === id)
  const treatmentBySlug = (slug: string) => catalog.treatments.find((t) => t.slug === slug)
  const concernById = (id: ConcernId) => catalog.concerns.find((c) => c.id === id)
  const articleBySlug = (slug: string) => catalog.articles.find((a) => a.slug === slug)
  const reviewsFor = (productId: string) => catalog.reviews.filter((r) => r.productId === productId)

  /** Producten, totaalprijs en duur van een protocol. De prijs is altijd de som van de delen. */
  const routineTotals = (routine: Routine) => {
    const items = productsByIds(routine.productIds)
    const price = items.reduce((sum, p) => sum + p.price, 0)
    return { items, price, months: monthsLabel(routine.lasts) }
  }

  const routineForConcern = (concern: Concern) =>
    routineById(concern.routineId) ?? catalog.routines[0]
  const treatmentForConcern = (concern: Concern) => treatmentBySlug(concern.treatmentSlug)
  const treatmentForRoutine = (routine: Routine) =>
    routine.treatmentSlug ? treatmentBySlug(routine.treatmentSlug) : undefined
  /** Het eerste protocol dat bij een behandeling hoort — voor de upsell na een afspraak. */
  const routineForTreatment = (slug: string) =>
    catalog.routines.find((r) => r.treatmentSlug === slug)
  const routinesWithProduct = (productId: string) =>
    catalog.routines.filter((r) => r.productIds.includes(productId))
  const productsForSkinType = (skinType: SkinType) =>
    catalog.products.filter((p) => p.skinTypes.includes(skinType))
  const concernsForProduct = (product: Product) =>
    catalog.concerns.filter((c) => product.skinTypes.includes(c.skinType))

  /** Gewogen gemiddelde van alle productreviews — één bron voor hero, reviewsectie en SEO. */
  const reviewCount = catalog.products.reduce((n, p) => n + p.reviews, 0)
  const shopRating = {
    count: reviewCount,
    average: reviewCount
      ? catalog.products.reduce((n, p) => n + p.rating * p.reviews, 0) / reviewCount
      : 0,
  }

  /** Merknamen als lijst — gebruik deze in copy en SEO, nooit handmatig uitschrijven. */
  const brandNames = catalog.brands.map((b) => b.name)

  return {
    ...catalog,
    productById,
    productsByIds,
    routineById,
    treatmentBySlug,
    concernById,
    articleBySlug,
    reviewsFor,
    routineTotals,
    routineForConcern,
    treatmentForConcern,
    treatmentForRoutine,
    routineForTreatment,
    routinesWithProduct,
    productsForSkinType,
    concernsForProduct,
    shopRating,
    brandNames,
  }
}

export type CatalogIndex = ReturnType<typeof createCatalogIndex>

/**
 * Controleert of alle verwijzingen bestaan. Een typfout in een id breekt anders
 * stil een protocol of aanbeveling. Geeft een lijst problemen terug (leeg = in orde).
 * De backend hoort dezelfde regels af te dwingen bij opslaan.
 */
export function catalogIssues(index: CatalogIndex): string[] {
  const issues: string[] = []
  const hasProduct = (id: string) => Boolean(index.productById(id))

  index.products.forEach((p) => {
    p.pairs.forEach((id) => !hasProduct(id) && issues.push(`${p.id}: pairs → onbekend product ${id}`))
    if (p.skinTypes.length === 0) issues.push(`${p.id}: geen huidthema-tag`)
  })
  index.routines.forEach((r) => {
    r.productIds.forEach((id) => !hasProduct(id) && issues.push(`${r.id}: onbekend product ${id}`))
    if (r.treatmentSlug && !index.treatmentBySlug(r.treatmentSlug)) {
      issues.push(`${r.id}: onbekende behandeling ${r.treatmentSlug}`)
    }
  })
  index.concerns.forEach((c) => {
    const routine = index.routineById(c.routineId)
    if (!routine) issues.push(`${c.id}: onbekend protocol ${c.routineId}`)
    if (!index.treatmentBySlug(c.treatmentSlug)) {
      issues.push(`${c.id}: onbekende behandeling ${c.treatmentSlug}`)
    }
    if (routine && !index.routineTotals(routine).items.some((p) => p.skinTypes.includes(c.skinType))) {
      issues.push(`${c.id}: protocol ${c.routineId} bevat geen product met tag ${c.skinType}`)
    }
  })
  index.articles.forEach((a) =>
    a.productIds?.forEach(
      (id) => !hasProduct(id) && issues.push(`artikel ${a.slug}: onbekend product ${id}`),
    ),
  )
  quizQuestions.forEach((q) =>
    q.options.forEach((o) => {
      if (o.concern && !index.concernById(o.concern)) {
        issues.push(`quiz ${q.id}: onbekende klacht ${o.concern}`)
      }
      if (o.routineId && !index.routineById(o.routineId)) {
        issues.push(`quiz ${q.id}: onbekend protocol ${o.routineId}`)
      }
    }),
  )
  return issues
}
